/**
 * Toggle-state poller - keeps the PWA's toggle runtime mirror
 * (`toggle/runtime.ts`) in sync with OPBridge's `/toggles/{consumer}`
 * library, so a toggle-mode FireButton shows the real lit-state on load /
 * reconnect even if a pedal (or another open tab) flipped it while this
 * tab was closed. Toggle flips are rare, event-driven taps (not a
 * continuous animation like an LFO), so this uses one flat cadence rather
 * than enginesStatePoll's idle/active split.
 */

import { bridges, getConnection } from './store.ts';
import type { BridgeConfig } from './storage.ts';
import { recordToggleLibrary } from '../toggle/runtime.ts';

const CONSUMER = 'opbuttons';
const POLL_INTERVAL_MS = 4_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;

/** Start the poller. Idempotent - calling twice is a no-op. */
export function startToggleStatePoll(): void {
  if (timer !== null) return;
  stopped = false;
  schedule(0);
}

/** Stop the poller. Used by tests + on tear-down. */
export function stopToggleStatePoll(): void {
  stopped = true;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => {
    timer = null;
    void tick();
  }, delayMs);
}

async function tick(): Promise<void> {
  if (stopped) return;
  // Every connected computer, not just the default (audit 2026-11-03 A-11):
  // a toggle aimed at STREAMPC lives in STREAMPC's library.
  const primary = pickDefaultConnected();
  const up = bridges().filter((b) => getConnection(b.id)?.status() === 'connected');
  await Promise.all(up.map(async (cfg) => {
    const conn = getConnection(cfg.id);
    if (!conn) return;
    try {
      const lib = await conn.httpClient().getToggles(CONSUMER);
      recordToggleLibrary(lib.toggles, cfg.id, cfg.id === primary?.id);
    } catch (err) {
      // 401 (scope missing), 404 (unshipped route on an older daemon), network
      // abort - all expected. Skip silently, same contract as every other
      // poller in this file's sibling modules; next tick retries.
      void err;
    }
  }));
  schedule(POLL_INTERVAL_MS);
}

function pickDefaultConnected(): BridgeConfig | null {
  // DEC-063: toggle state comes from the primary, same as engine state.
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((c) => c.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  if (conn && conn.status() === 'connected') return primary;
  return null;
}
